import { useState } from "react";
import { toast } from "sonner";
import { Linkedin, Twitter, Facebook, Mail, Link2, Check } from "lucide-react";

const WhatsAppIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M20.52 3.48A11.85 11.85 0 0 0 12.03 0C5.46 0 .13 5.32.13 11.88c0 2.09.55 4.13 1.6 5.93L0 24l6.35-1.66a11.87 11.87 0 0 0 5.68 1.45h.01c6.56 0 11.88-5.32 11.88-11.88 0-3.17-1.23-6.15-3.4-8.43zM12.04 21.6h-.01a9.72 9.72 0 0 1-4.95-1.36l-.36-.21-3.77.99 1.01-3.67-.23-.38a9.7 9.7 0 0 1-1.49-5.09c0-5.36 4.37-9.72 9.74-9.72 2.6 0 5.04 1.01 6.88 2.85a9.66 9.66 0 0 1 2.85 6.87c0 5.37-4.37 9.72-9.67 9.72zm5.34-7.28c-.29-.15-1.73-.85-2-.95-.27-.1-.47-.15-.66.14-.2.29-.76.95-.94 1.15-.17.2-.34.22-.63.07-.29-.15-1.23-.45-2.34-1.44-.86-.77-1.44-1.72-1.61-2-.17-.29-.02-.44.13-.59.13-.13.29-.34.44-.51.14-.17.19-.29.29-.49.1-.2.05-.37-.02-.51-.07-.15-.66-1.59-.91-2.18-.24-.57-.48-.49-.66-.5l-.56-.01a1.07 1.07 0 0 0-.78.36c-.27.29-1.02 1-1.02 2.44 0 1.44 1.05 2.83 1.2 3.02.15.2 2.06 3.15 4.98 4.42.7.3 1.24.48 1.66.61.7.22 1.33.19 1.83.11.56-.08 1.73-.7 1.97-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.2-.56-.34z"/>
  </svg>
);

/**
 * Share row for blog posts.
 * `url` and `title` are the current post's absolute URL and title.
 */
export default function ShareRow({ url, title }) {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const linkedin = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
  const twitter = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`;
  const facebook = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
  const whatsapp = `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`;
  const mailto = `mailto:?subject=${encodedTitle}&body=${encodedUrl}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — please copy manually.");
    }
  };

  const items = [
    { key: "linkedin", href: linkedin, label: "Share on LinkedIn", icon: <Linkedin size={16} /> },
    { key: "twitter", href: twitter, label: "Share on X (Twitter)", icon: <Twitter size={16} /> },
    { key: "facebook", href: facebook, label: "Share on Facebook", icon: <Facebook size={16} /> },
    { key: "whatsapp", href: whatsapp, label: "Share on WhatsApp", icon: <WhatsAppIcon /> },
    { key: "email", href: mailto, label: "Share via Email", icon: <Mail size={16} /> },
  ];

  return (
    <div className="my-8" data-testid="share-row">
      <div className="eyebrow mb-3">Share this</div>
      <div className="flex flex-wrap gap-2">
        {items.map((it) => (
          <a
            key={it.key}
            href={it.href}
            target={it.key === "email" ? undefined : "_blank"}
            rel={it.key === "email" ? undefined : "noopener noreferrer"}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border hairline text-ink hover:border-ink hover:bg-paper-raised transition-colors text-[13px]"
            title={it.label}
            aria-label={it.label}
            data-testid={`share-${it.key}`}
          >
            {it.icon}
            <span className="hidden sm:inline">{it.label.replace("Share on ", "").replace("Share via ", "")}</span>
          </a>
        ))}
        <button
          type="button"
          onClick={copy}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border hairline text-ink hover:border-ink hover:bg-paper-raised transition-colors text-[13px]"
          aria-label="Copy link"
          data-testid="share-copy"
        >
          {copied ? <Check size={16} /> : <Link2 size={16} />}
          <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
        </button>
      </div>
    </div>
  );
}
